import { useCallback, useEffect, useRef, useState } from 'react';

import {
  acceptVideoFiles,
  type ApiScanVideoActiveWorkItem,
  type ApiScanVideoProgress,
  buildSelectedVideoFileFromActiveWorkItem,
  getDurationValidationError,
  getOverflowNotice,
} from '@kaiizen/kai-pro-api';

import { type PickerSelectedVideoFile } from './aiScanVideoPickerTypes';
import { readBrowserVideoFileMetadata } from './readBrowserVideoFileDuration';

let fileIdSeq = 0;
const nextFileId = () => `vid-${++fileIdSeq}`;

const STEP_FLASH_MS = 320;

/**
 * Owns the multi-file picker state for AIScanVideoUploadModal: selected rows,
 * async duration checks, overflow notices, and per-row upload/job progress.
 */
export const useAIScanVideoFilePicker = () => {
  const [selectedFiles, setSelectedFiles] = useState<PickerSelectedVideoFile[]>([]);
  const [overflowNotice, setOverflowNotice] = useState<string | undefined>(undefined);
  const selectedFilesRef = useRef<PickerSelectedVideoFile[]>([]);
  const overflowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepFlashTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  const clearOverflowTimeout = useCallback(() => {
    if (overflowTimeoutRef.current) {
      clearTimeout(overflowTimeoutRef.current);
      overflowTimeoutRef.current = null;
    }
  }, []);

  const clearStepFlash = useCallback((id: string) => {
    const timeout = stepFlashTimeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      stepFlashTimeoutsRef.current.delete(id);
    }
    setSelectedFiles(current => current.map(row => (row.id === id ? { ...row, stepFlash: false } : row)));
  }, []);

  useEffect(() => () => {
    clearOverflowTimeout();
    stepFlashTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    stepFlashTimeoutsRef.current.clear();
  }, [clearOverflowTimeout]);

  const showOverflowNotice = useCallback((notice: string | undefined) => {
    clearOverflowTimeout();
    setOverflowNotice(notice);
    if (!notice) return;
    overflowTimeoutRef.current = setTimeout(() => {
      setOverflowNotice(undefined);
      overflowTimeoutRef.current = null;
    }, 4100);
  }, [clearOverflowTimeout]);

  const queueDurationCheck = useCallback((row: PickerSelectedVideoFile) => {
    if (row.state !== 'checking' || !(row.file instanceof File)) return;

    void readBrowserVideoFileMetadata(row.file).then(metadata => {
      setSelectedFiles(current =>
        current.map(candidate => {
          if (candidate.id !== row.id || candidate.state !== 'checking') return candidate;

          const durationError = getDurationValidationError(metadata.durationSeconds);
          if (durationError) {
            return {
              ...candidate,
              durationSeconds: metadata.durationSeconds,
              width: metadata.width,
              height: metadata.height,
              videoRecordedAt: metadata.videoRecordedAt,
              state: 'bad' as const,
              reason: durationError,
            };
          }

          return {
            ...candidate,
            durationSeconds: metadata.durationSeconds,
            width: metadata.width,
            height: metadata.height,
            videoRecordedAt: metadata.videoRecordedAt,
            state: 'validated' as const,
          };
        })
      );
    });
  }, []);

  const acceptFiles = useCallback((fileList: FileList | File[] | null | undefined) => {
    const files = Array.from(fileList ?? []);
    if (!files.length) return;

    const { rows, accepted, overflowCount } = acceptVideoFiles(
      selectedFilesRef.current,
      files,
      nextFileId,
    );
    selectedFilesRef.current = rows;
    setSelectedFiles(rows);
    accepted.forEach(queueDurationCheck);
    showOverflowNotice(getOverflowNotice(overflowCount));
  }, [queueDurationCheck, showOverflowNotice]);

  const removeFile = useCallback((id: string) => {
    clearStepFlash(id);
    setSelectedFiles(current => current.filter(row => row.id !== id));
  }, [clearStepFlash]);

  const clearSelection = useCallback(() => {
    clearOverflowTimeout();
    stepFlashTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    stepFlashTimeoutsRef.current.clear();
    setOverflowNotice(current => (current === undefined ? current : undefined));
    selectedFilesRef.current = [];
    setSelectedFiles(current => (current.length === 0 ? current : []));
  }, [clearOverflowTimeout]);

  const hydrateFromActiveWorkItems = useCallback((items: readonly ApiScanVideoActiveWorkItem[]) => {
    clearOverflowTimeout();
    setOverflowNotice(undefined);
    const rows = items.map(buildSelectedVideoFileFromActiveWorkItem) as PickerSelectedVideoFile[];
    selectedFilesRef.current = rows;
    setSelectedFiles(rows);
  }, [clearOverflowTimeout]);

  const markUploading = useCallback((ids: readonly string[]) => {
    const idSet = new Set(ids);
    setSelectedFiles(current =>
      current.map(row =>
        idSet.has(row.id) && row.state === 'validated'
          ? { ...row, state: 'uploading' as const, uploadPct: 0 }
          : row
      )
    );
  }, []);

  const setUploadProgress = useCallback((id: string, uploadPct: number) => {
    setSelectedFiles(current => current.map(row => (row.id === id ? { ...row, uploadPct } : row)));
  }, []);

  const markFileBad = useCallback((id: string, reason: string) => {
    setSelectedFiles(current => current.map(row => row.id === id ? { ...row, state: 'bad' as const, reason } : row));
  }, []);

  const markUploaded = useCallback((id: string, videoId: string) => {
    setSelectedFiles(current =>
      current.map(row =>
        row.id === id
          ? {
            ...row,
            state: 'uploaded' as const,
            videoId,
            uploadPct: 100,
          }
          : row
      )
    );
  }, []);

  const markAnalyzing = useCallback((id: string, videoId: string) => {
    setSelectedFiles(current =>
      current.map(row =>
        row.id === id
          ? {
            ...row,
            state: 'analyzing' as const,
            videoId,
            uploadPct: 100,
            jobStepIndex: 0,
          }
          : row
      )
    );
  }, []);

  const syncJobProgress = useCallback((id: string, polled: ApiScanVideoProgress) => {
    let stepAdvanced = false;
    let clearFlash = false;

    setSelectedFiles(current =>
      current.map(row => {
        if (row.id !== id) return row;

        const nextStepIndex = polled.progress?.stepIndex ?? row.jobStepIndex ?? 0;
        const prevStepIndex = row.jobStepIndex ?? 0;
        stepAdvanced = nextStepIndex > prevStepIndex && prevStepIndex > 0;

        if (polled.lifecycleStatus === 'completed') {
          clearFlash = true;
          return {
            ...row,
            state: 'analyzed' as const,
            jobStepIndex: nextStepIndex,
            stepFlash: false,
            uploadPct: 100,
          };
        }

        if (polled.lifecycleStatus === 'failed') {
          clearFlash = true;
          return {
            ...row,
            state: 'analysisFailed' as const,
            jobStepIndex: nextStepIndex,
            stepFlash: false,
            reason: polled.error ?? 'Analysis failed',
          };
        }

        return {
          ...row,
          state: 'analyzing' as const,
          jobStepIndex: nextStepIndex,
          stepFlash: stepAdvanced ? true : row.stepFlash,
        };
      })
    );

    const existingTimeout = stepFlashTimeoutsRef.current.get(id);
    if (existingTimeout) clearTimeout(existingTimeout);

    if (clearFlash) {
      stepFlashTimeoutsRef.current.delete(id);
      return;
    }

    if (stepAdvanced) {
      const timeout = setTimeout(() => clearStepFlash(id), STEP_FLASH_MS);
      stepFlashTimeoutsRef.current.set(id, timeout);
    }
  }, [clearStepFlash]);

  return {
    selectedFiles,
    overflowNotice,
    acceptFiles,
    removeFile,
    clearSelection,
    hydrateFromActiveWorkItems,
    markUploading,
    setUploadProgress,
    markFileBad,
    markUploaded,
    markAnalyzing,
    syncJobProgress,
  };
};
